"use client";

/**
 * CollabV3ScheduleRow 컴포넌트
 * @description 날짜 줄(앱 `CollabDetailWhereWhen` 의 위 줄). 같은 날이면 아랫줄이 시간대, 여러 날이면
 *   `Anytime during the period`, 시작이 없으면 `Join anytime`. 2.0 콜랩은 날짜만(없으면 줄째 숨김).
 *   표시 시간대는 `CollabV3TimeZone` 이
 *   정하므로 클라이언트 컴포넌트다. 웹에서는 캘린더에 넣지 않는다(앱의 OS 일정 화면 자리).
 * @example <CollabV3ScheduleRow schedule={detail.schedule} />
 */
import { CalendarDays } from "lucide-react";
import { memo, useMemo } from "react";

import { collabV3DetailStyles } from "./CollabV3Detail.styles";
import type { CollabV3ScheduleRowProps } from "./CollabV3Detail.types";
import { useCollabV3TimeZone } from "./CollabV3TimeZone";

import { COLLAB_V3_COPY } from "../../constants/collabV3Copy";
import { formatSchedule } from "../../utils/collabV3Format";

const CollabV3ScheduleRow = memo<CollabV3ScheduleRowProps>(({ schedule }) => {
  const styles = collabV3DetailStyles.row;
  const timeZone = useCollabV3TimeZone();
  const when = useMemo(
    () =>
      formatSchedule(
        schedule.startAt,
        schedule.endAt,
        timeZone,
        schedule.isLegacy,
      ),
    [schedule, timeZone],
  );
  // 2.0 콜랩에 날짜가 하나도 없으면 줄을 숨긴다 — 상시 문구는 3.0 의 개념이다(앱과 같다)
  if (schedule.isLegacy && !when.title) return null;
  const title = when.title ?? COLLAB_V3_COPY.schedule.always;
  const meta = when.isPeriod ? COLLAB_V3_COPY.schedule.anytime : when.subtitle;

  return (
    <div className={styles.root}>
      <CalendarDays aria-hidden className={styles.icon} />
      <span className={styles.texts}>
        <span className={styles.title}>{title}</span>
        {meta ? <span className={styles.meta}>{meta}</span> : null}
      </span>
    </div>
  );
});

CollabV3ScheduleRow.displayName = "CollabV3ScheduleRow";

export default CollabV3ScheduleRow;
